if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    init() {
      this.selectors = {
        error: ".m-product-form-message",
        variantIdInput: '[name="id"]',
        pickerFields: ["[data-picker-field]"],
        optionNodes: [".m-product-option--node"],
        productSku: "[data-variant-sku]",
        productAvailability: "[data-availability]",
        shareUrlInput: "[data-share-url]",
        stockCountdown: ".prod__stock-countdown",
        productWrapper: ".m-main-product--wrapper",
      };

      this.productWrapper = this.closest(this.selectors.productWrapper);
      this.productForm = this.productWrapper.querySelector("product-form");
      this.domNodes = queryDomNodes(this.selectors, this.productWrapper);

      const themeProducts = window._themeProducts || {};
      const { productId, sectionId, productUrl, hasOnlyDefaultVariant, showFeaturedMedia } = this.dataset;

      this.productId = productId;
      this.sectionId = sectionId;
      this.productUrl = productUrl;
      this.hasOnlyDefaultVariant = hasOnlyDefaultVariant === "true";
      this.showFeaturedMedia = showFeaturedMedia === "true";

      this.variantData = this.getVariantData();
      this.productData = Object.assign(this.getProductJson(), themeProducts[productId]);

      this.handleQueryString();
      this.handleVariantGroupImages();
      this.selectInitialVariant();
      this.setupEventListeners();
      this.initOptionSwatches();
    }

    handleQueryString() {
      const hasQueryString = window.location.search.includes("?variant=");
      this.selectedVariantDefault =
        this.dataset.disableSelectedVariantDefault === "true" &&
        this.productData &&
        this.productData.variants.length > 1 &&
        !hasQueryString;

      if (this.selectedVariantDefault) {
        this.disableSelectedVariantDefault();
      }
    }

    handleVariantGroupImages() {
      this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === "true";
      if (this.enableVariantGroupImages) {
        this.variantGroupImages = this.getVariantGroupImageData();
      }
    }

    selectInitialVariant() {
      this.selectedVariant = this.productWrapper.querySelector(this.selectors.variantIdInput);
      const selectedVariantId = Number(this.selectedVariant.value);
      this.currentVariant = this.productData.variants.find((variant) => variant.id === selectedVariantId);
      this.productData.current_variant_id = this.currentVariant && this.currentVariant.id;
      this.productData.initialVariant = this.currentVariant;

      if (this.currentVariant) {
        this.getDataImageVariant(this.currentVariant.id);
        this.hideSoldOutAndUnavailableOptions();
        this.updateButton(!this.currentVariant.available);
      }
    }

    setupEventListeners() {
      this.hasSetupMediaData = false;
      const mediaGalleryHandler = () => this.getMediaGallery();
      mediaGalleryHandler();
      document.addEventListener("matchMobile", mediaGalleryHandler);
      document.addEventListener("unmatchMobile", mediaGalleryHandler);

      this.addEventListener("change", this.onVariantChange);
    }

    async getMediaGallery() {
      await this.waitForMediaGallery();
      this.setupMediaGallery();
    }

    waitForMediaGallery() {
      return new Promise((resolve) => {
        let isInitialized = false;

        const checkMediaGallery = () => {
          this.mediaGallery = this.productWrapper.querySelector("media-gallery");
          this.mediaGalleryMobile = this.productWrapper.querySelector("media-gallery-mobile");
          if (this.mediaGallery && this.mediaGallery.view === "featured-product") {
            if (this.mediaGallery && this.mediaGallery.mediaMode) {
              isInitialized = true;
              resolve();
            } else {
              setTimeout(checkMediaGallery, 100);
            }
          } else {
            if (
              this.mediaGallery &&
              this.mediaGalleryMobile &&
              this.mediaGallery.mediaMode &&
              this.mediaGalleryMobile.mediaMode
            ) {
              isInitialized = true;
              resolve();
            } else {
              setTimeout(checkMediaGallery, 100);
            }
          }
        };

        checkMediaGallery();
      });
    }

    setupMediaGallery() {
      const isMobile = MinimogTheme.config.mqlMobile;
      const effectiveMediaGallery = isMobile ? this.mediaGalleryMobile || this.mediaGallery : this.mediaGallery;
      this.media = effectiveMediaGallery;
      if (!this.hasSetupMediaData) {
        if (isMobile || effectiveMediaGallery.mediaMode === "slider") {
          this.slides = effectiveMediaGallery.slider && effectiveMediaGallery.slider.slides;
          this.slidesNav = effectiveMediaGallery.navSlider && effectiveMediaGallery.navSlider.slides;
        } else {
          this.mediaItems = effectiveMediaGallery.querySelectorAll(".m-product-media--item");
        }

        this.layout = !isMobile ? effectiveMediaGallery.layout : undefined;
      }

      if (!this.showFeaturedMedia || (this.enableVariantGroupImages && this.variantGroupImages.enable)) {
        this.updateMedia();
      }
      this.hasSetupMediaData = true;
    }

    disableSelectedVariantDefault() {
      // Reset radio options
      this.querySelectorAll('input[type="radio"]').forEach((radioOption) => {
        radioOption.checked = false;
      });

      // Clear data-selected-value for radio picker fields
      this.querySelectorAll("[data-picker-field='radio']").forEach((radio) => {
        radio.setAttribute("data-selected-value", "");
      });

      // Reset select elements and clear data-selected-value for select picker fields
      this.querySelectorAll("[data-picker-field='select']").forEach((pickerSelect) => {
        const selectElm = pickerSelect.querySelector("select");
        if (selectElm) selectElm.value = "";
        pickerSelect.setAttribute("data-selected-value", "");
      });

      // Clear text content for selected option labels
      this.querySelectorAll(".option-label--selected").forEach((label) => {
        label.textContent = "";
      });

      // Reset variant ID if applicable
      const variantIdEl = this.productForm && this.productForm.querySelector("[name='id']");
      if (variantIdEl && !this.hasOnlyDefaultVariant) {
        variantIdEl.value = "";
      }
    }

    setProductAsUnavailable() {
      this.updateButton(true); // Disable button, clear text, mark as unavailable
      this.setUnavailable();
    }

    updateProductDetailsForVariant() {
      // Fetch additional data and update UI components to reflect the selected variant
      this.getDataImageVariant(this.currentVariant.id);
      this.updateMedia();
      this.updateBrowserHistory();
      this.updateVariantInput();
      this.updateProductMeta();
      this.updatePrice();

      // Update the button based on availability of the current variant
      const isSoldOut = !this.currentVariant.available;
      const buttonText = isSoldOut ? window.MinimogStrings.soldOut : "";
      this.updateButton(isSoldOut, buttonText);

      // Hide options that are sold out or unavailable
      this.hideSoldOutAndUnavailableOptions();
    }

    onVariantChange() {
      // Retrieve selected options and variant
      this.getSelectedOptions();
      this.getSelectedVariant();

      // Always perform these updates on variant change
      this.updatePickupAvailability();
      this.removeErrorMessage();

      // If there's no current variant, set the product as unavailable and return early
      if (!this.currentVariant) {
        this.setProductAsUnavailable();
        return;
      }

      // Update the product details for the selected variant
      this.updateProductDetailsForVariant();

      // Emit a custom event signaling a variant change
      window.MinimogEvents.emit(`${this.productId}__VARIANT_CHANGE`, this.currentVariant, this);
    }

    getDataImageVariant(variantId) {
      // Early return if variantGroupImages is not set or disabled to avoid unnecessary processing
      if (!this.variantGroupImages || !this.variantGroupImages.enable) {
        return;
      }

      // Attempt to find the current variant media by matching the variant ID
      const currentVariantMedia = this.variantGroupImages.mapping.find((variant) => Number(variant.id) === variantId);

      // Update currentVariantMedia if a matching variant is found
      if (currentVariantMedia) {
        this.currentVariantMedia = currentVariantMedia.media;
      }
    }

    getProductJson() {
      try {
        const productDataElement = this.productWrapper.querySelector("#productData[type='application/json']");
        if (!productDataElement) {
          throw new Error("Fallback product data element not found.");
        }
        return JSON.parse(productDataElement.textContent);
      } catch (error) {
        console.error("Error loading fallback product data:", error);
        // Return a default value or further handle the error as needed
      }
    }

    getSelectedVariant() {
      let variant;
      let options = [...this.options];

      // Attempt to find a variant matching the current options, reducing options from the end if necessary
      for (let attempt = 0; attempt < this.options.length; attempt++) {
        variant = getVariantFromOptionArray(this.productData, options);

        // If a variant is found, or no options are left, break out of the loop
        if (variant || options.length === 0) {
          break;
        }

        // Remove the last option and try again
        options.pop();
      }

      // If a variant is found, update the selected options and the current variant
      if (variant) {
        this.options = [...variant.options]; // Update options to match found variant
        this.updateSelectedOptions();
      }

      // Update the current variant, regardless of whether a match was found
      this.currentVariant = variant;
    }

    getSelectedOptions() {
      // Convert NodeList to Array for easier manipulation
      const pickerFields = Array.from(this.querySelectorAll("[data-picker-field]"));

      // Map each picker field to its corresponding value based on its type
      this.options = pickerFields
        .map((field) => {
          const type = field.dataset.pickerField;

          // For radio button fields, find the checked radio and return its value
          if (type === "radio") {
            const checkedRadio = field.querySelector("input:checked");
            return checkedRadio ? checkedRadio.value : undefined;
          }

          // For select fields, return the selected option's value
          if (field.querySelector("select")) {
            return field.querySelector("select").value;
          }

          // Return undefined if no value is found (keeps the array's structure consistent)
          return undefined;
        })
        .filter((option) => option !== undefined); // Filter out undefined values to clean up the result
    }

    updateSelectedOptions() {
      this.domNodes.pickerFields.forEach((field, index) => {
        const selectedValue = field.dataset.selectedValue;
        if (selectedValue !== this.options[index]) {
          const selectedOption = field.querySelector(`input[value="${this.options[index].replace(/["\\]/g, "\\$&")}"]`);
          if (selectedOption) {
            selectedOption.checked = true;
            field.updateSelectedValue();
          }
        }
      });
    }

    removeLoading(mediaElement) {
      if (mediaElement) {
        mediaElement.removeAttribute("data-media-loading");
        mediaElement.firstElementChild.style.opacity = 1;
      }
    }

    handleImageZoom(mediaItems, mediaContext) {
      const customData = mediaItems.map((item) => {
        const mediaType = item.dataset.mediaType;
        if (mediaType === "image") {
          const mediaDataItem = item.querySelector(".m-product-media");
          const mediaData = mediaDataItem.dataset;
          return {
            src: mediaData.mediaSrc,
            width: parseInt(mediaData.mediaWidth),
            height: parseInt(mediaData.mediaHeight),
            alt: mediaData.mediaAlt,
            id: item.dataset.index,
          };
        } else {
          return {
            html: `<div class="pswp__${mediaType}">${item.innerHTML}</div>`,
            type: mediaType,
            id: item.dataset.index,
          };
        }
      });

      // Init Photoswipe
      if (mediaContext && mediaContext.lightbox) {
        mediaContext.lightbox.destroy();
        mediaContext.handlePhotoswipe(customData);
        mediaContext.initPhotoswipe();
      }
    }

    updateMedia() {
      if (!this.currentVariant) return;

      // Define main and navigation items arrays
      let mainItems = [],
        navItems = [];

      const reorderItems = (referenceItems, items, isNav = false, isSlider = false) => {
        let reorderedItems = [];

        referenceItems.forEach((referenceItem, index) => {
          const foundItem = items.find(
            (item) => item.querySelector("[data-media-id]").dataset.mediaId === referenceItem
          );
          if (foundItem) {
            foundItem.dataset.index = index;
            foundItem.dataset.swiperSlideIndex = index;
            reorderedItems.push(foundItem);
          }
        });

        items.forEach((item) => {
          const dataIdMedia = item.querySelector("[data-media-id]").dataset.mediaId;
          if (!referenceItems.includes(dataIdMedia) && !reorderedItems.includes(item)) {
            item.dataset.index = reorderedItems.length;
            item.dataset.swiperSlideIndex = reorderedItems.length;
            reorderedItems.push(item);
          }
        });

        isSlider &&
          reorderedItems.forEach((item) => {
            if (isNav) {
              item.classList.toggle("swiper-slide-thumb-active", item.dataset.swiperSlideIndex === "0");
            } else {
              item.classList.toggle("swiper-slide-active", item.dataset.swiperSlideIndex === "0");
            }
          });

        return reorderedItems;
      };

      // Common function to process slides
      const processItems = (currentItems, items) => {
        currentItems.forEach((slide) => {
          const dataIdMedia = slide.querySelector("[data-media-id]").dataset.mediaId;

          const isCurrentVariantMedia =
            this.currentVariantMedia &&
            this.currentVariantMedia.length > 0 &&
            this.currentVariantMedia.includes(dataIdMedia);
          const isNotImageType = !slide.classList.contains("media-type-image");

          if (
            isCurrentVariantMedia ||
            isNotImageType ||
            !this.currentVariantMedia ||
            this.currentVariantMedia.length === 0
          ) {
            items.push(slide);
          }
        });
      };

      // Process based on media mode
      if (this.variantGroupImages && this.variantGroupImages.enable) {
        if (this.media.mediaMode === "slider" && this.slides) {
          // Re-render main media
          processItems(this.slides, mainItems);
          const reorderedMainItems = reorderItems(this.currentVariantMedia, mainItems, false, true);

          // Update slider with new slides
          this.media.slider.removeAllSlides();
          this.media.slider.appendSlide(reorderedMainItems);
          this.media.slider.slideToLoop(this.layout === "layout-7" ? 1 : 0);
          this.media.handleSlideChange();

          // Re-render nav media if available
          if (this.slidesNav) {
            processItems(this.slidesNav, navItems);
            const reorderedNavItems = reorderItems(this.currentVariantMedia, navItems, true, true);
            this.media.navSlider && this.media.navSlider.removeAllSlides();
            this.media.navSlider && this.media.navSlider.appendSlide(reorderedNavItems);
            this.media.navSlider && this.media.navSlider.slideToLoop(0);
          }
        } else {
          // Ensure mediaWrapper is available before proceeding.
          const mediaWrapper = this.media.querySelector(".m-product-media--list");
          if (!mediaWrapper) return;

          // Use a more functional approach to filter and process items.
          processItems(this.mediaItems, mainItems);

          const reorderedItems = reorderItems(this.currentVariantMedia, mainItems);

          // Clear the mediaWrapper content once before appending new items.
          mediaWrapper.innerHTML = "";

          // Process and append the main items to the mediaWrapper.
          reorderedItems.forEach((item, index) => {
            // Additional layout-specific adjustments can be made here.
            if (this.layout === "layout-2") {
              item.classList.remove("m-col-span-2");
              if (index % 3 === 0) {
                item.classList.add("m-col-span-2");
              }
            }
            mediaWrapper.append(item); // Append each item to the mediaWrapper.
          });
        }
      } else {
        // Initialize mediaSize to 0 by default
        let mediaSize = 0;

        // Check if this.media and this.media.dataset.mediaSize exist before parsing
        if (this.media && "mediaSize" in this.media.dataset) {
          mediaSize = parseInt(this.media.dataset.mediaSize, 10);
        }

        // Proceed only if mediaSize is a positive number
        if (mediaSize > 0) {
          this.media.setActiveMedia(this.currentVariant);
        }
      }
      // Common operations after media update
      this.removeLoading(this.media);
      this.handleImageZoom(mainItems, this.media);
    }

    updateBrowserHistory() {
      if (!this.currentVariant || this.dataset.updateUrl === "false") return;
      window.history.replaceState({}, "", `${this.productUrl}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.sectionId}, #product-form-installment`);

      productForms.forEach((productForm) => {
        const variantIdInput = productForm.querySelector(this.selectors.variantIdInput);

        if (variantIdInput) {
          variantIdInput.value = this.currentVariant.id;
          variantIdInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = this.productWrapper.querySelector("pickup-availability");

      if (!pickUpAvailability || !this.currentVariant || !this.currentVariant.available) {
        if (pickUpAvailability) {
          pickUpAvailability.removeAttribute("available");
          pickUpAvailability.innerHTML = "";
        }
        return;
      }

      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    }

    removeErrorMessage() {
      const section = this.closest("section");
      if (!section) return;

      const productForm = section.querySelector("product-form");
      if (productForm) {
        productForm.handleErrorMessage();
      }
    }

    updatePrice() {
      const classes = {
        onSale: "m-price--on-sale",
        soldOut: "m-price--sold-out",
      };

      const selectors = {
        priceWrapper: ".m-price",
        salePrice: ".m-price-item--sale",
        compareAtPrice: [".m-price-item--regular"],
        unitPrice: ".m-price__unit",
        unitPriceWrapper: ".m-price__unit-wrapper",
        saleBadge: ".m-price__badge-sale",
        saleAmount: "[data-saved-price]",
      };

      const mainProductPrices = this.productWrapper.querySelector(".main-product__block-price");
      if (!mainProductPrices) return;

      const moneyFormat = window.MinimogSettings.money_format;
      const { priceWrapper, salePrice, unitPrice, compareAtPrice, saleBadge, saleAmount, unitPriceWrapper } =
        queryDomNodes(selectors, mainProductPrices);

      const { compare_at_price, price, unit_price_measurement, available } = this.currentVariant;
      const saleBadgeType = priceWrapper.dataset.saleBadgeType;

      const onSale = compare_at_price && compare_at_price > price;
      const soldOut = !available;

      priceWrapper.classList.toggle(classes.onSale, onSale);
      priceWrapper.classList.toggle(classes.soldOut, soldOut);
      priceWrapper.classList.remove("m:visibility-hidden");

      if (salePrice) salePrice.innerHTML = formatMoney(price, moneyFormat);

      if (compareAtPrice) {
        compareAtPrice.forEach((item) => {
          const displayPrice = onSale ? compare_at_price : price;
          item.innerHTML = formatMoney(displayPrice, moneyFormat);
        });
      }

      if (saleBadge && onSale && saleBadgeType !== "text") {
        let value;
        if (saleBadgeType === "fixed_amount") {
          value = formatMoney(compare_at_price - price, moneyFormat);
        } else {
          const saving = Math.round(((compare_at_price - price) * 100) / compare_at_price) + "%";
          value = saving;
        }
        if (saleAmount) saleAmount.innerHTML = value;
      }

      if (unit_price_measurement && unitPrice) {
        unitPriceWrapper.classList.remove("m:hidden");
        const unitPriceContent = `<span>${formatMoney(
          this.currentVariant.unit_price,
          moneyFormat
        )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
        unitPrice.innerHTML = unitPriceContent;
      } else {
        unitPriceWrapper.classList.add("m:hidden");
      }
    }

    _getBaseUnit = () => {
      const { reference_value, reference_unit } = this.currentVariant.unit_price_measurement;
      return reference_value === 1 ? reference_unit : `${reference_value}${reference_unit}`;
    };

    updateButton(disable = true, text) {
      const productForms = document.querySelectorAll(`.product-form-${this.sectionId}`);
      if (!productForms.length) return;

      productForms.forEach((productForm) => {
        const addButton = productForm.querySelector('[name="add"]');
        const dynamicCheckout = productForm.querySelector(".m-product-dynamic-checkout");
        const addButtonText = addButton.querySelector(".m-add-to-cart--text");

        if (!addButton) return;

        const cartIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="19" viewBox="0 0 16 19" fill="none" style="display: inline-block; flex-shrink: 0; width: 16px; height: 19px;"><path d="M8 0L0.256348 13.1318H15.7437L8 0ZM6.87626 0.541482L0 5.44137V12.2023L6.87626 0.541482ZM9.12365 0.541482L16 12.2023V5.44132L9.12365 0.541482ZM3.36209 4.39569V5.32846L2.99313 5.74291V5.01754C2.87261 5.15626 2.74565 5.29014 2.62857 5.43131C2.7823 5.44385 2.73657 5.66379 2.72452 5.76321C2.64861 6.17149 2.37 6.51568 2.14174 6.82459C2.05726 6.93351 1.9703 7.04188 1.88052 7.14601C1.67835 7.29158 1.51139 7.33396 1.41204 7.08337C1.42483 6.74915 1.579 6.43172 1.76187 6.18566L1.99178 5.84665C1.9897 5.95207 1.99613 6.05945 1.98865 6.1637C1.88691 6.34982 1.69448 6.56873 1.7383 6.78914C2.0757 6.82656 2.26961 6.32404 2.3697 6.07927C2.40587 5.9611 2.46135 5.84558 2.30804 5.84661V5.5356L3.36209 4.39569ZM12.5851 4.4475L14.5353 6.77938V7.0903L13.2177 5.53569V5.89842L12.9542 5.58749C12.9475 5.40981 12.8923 5.22883 12.8488 5.0693C12.7679 4.9436 12.6766 4.82509 12.5853 4.70666L12.5851 4.4475ZM8.00304 7.138C8.36696 7.138 8.64359 7.20346 8.83296 7.33439C9.02232 7.46398 9.117 7.65442 9.117 7.90572C9.117 8.07372 9.07 8.21391 8.976 8.32629C8.88336 8.43872 8.74571 8.52204 8.56304 8.57624C8.7671 8.63182 8.92087 8.72244 9.02435 8.84812C9.12907 8.97234 9.18145 9.12967 9.18148 9.32011C9.18148 9.61368 9.08209 9.83589 8.8833 9.98674C8.68591 10.1361 8.39249 10.2108 8.00304 10.2108C7.61223 10.2108 7.31678 10.1361 7.1167 9.9867C6.91791 9.83591 6.81852 9.6137 6.81852 9.32007C6.81852 9.12969 6.87026 8.97235 6.97374 8.84808C7.07843 8.7224 7.23285 8.63177 7.437 8.5762C7.25439 8.52205 7.11675 8.43875 7.02409 8.32629C6.93145 8.21389 6.8851 8.07371 6.88504 7.90576C6.8851 7.65583 6.9798 7.46541 7.16913 7.33448C7.35846 7.20355 7.63644 7.1381 8.00304 7.13813V7.138ZM8.00304 7.61804C7.87954 7.61804 7.78417 7.6498 7.71696 7.71331C7.65116 7.77679 7.61826 7.8667 7.61826 7.98304C7.61826 8.10078 7.65116 8.19138 7.71696 8.25483C7.78417 8.31834 7.87954 8.35009 8.00304 8.35009C8.12391 8.35009 8.21725 8.31834 8.28304 8.25483C8.34884 8.19138 8.38174 8.1008 8.38174 7.98308C8.38174 7.86537 8.34884 7.77546 8.28304 7.71335C8.21725 7.64981 8.12391 7.61804 8.00304 7.61804ZM8.00304 8.82808C7.85797 8.82808 7.74652 8.8671 7.6687 8.94514C7.59072 9.02318 7.55174 9.13492 7.55174 9.28038C7.55174 9.42587 7.59072 9.53761 7.6687 9.61562C7.74652 9.69235 7.85797 9.73071 8.00304 9.73071C8.14672 9.73071 8.25684 9.69236 8.33339 9.61567C8.40991 9.53757 8.44817 9.42579 8.44817 9.28034C8.44817 9.13357 8.40991 9.02182 8.33339 8.9451C8.25687 8.86706 8.14675 8.82804 8.00304 8.82804V8.82808ZM0.726087 13.8168L8 19L15.2739 13.8168H0.726087ZM6.78783 14.8114H9.15948V15.0187C8.53313 15.2731 8.22113 15.5709 7.9473 16.1069H7.26209C7.57096 15.5159 7.89452 15.3485 8.42165 15.0187H6.78774L6.78783 14.8114Z" fill="currentColor"/></svg>';

        const disableButton = () => {
          addButton.setAttribute("disabled", "disabled");
          addButton.classList.add("disabled");
          dynamicCheckout && dynamicCheckout.classList.add("disabled");
          if (text) addButtonText.innerHTML = text;
        };

        const enableButton = () => {
          addButton.removeAttribute("disabled");
          addButton.classList.remove("disabled");
          dynamicCheckout && dynamicCheckout.classList.remove("disabled");
          if (!addButtonText.style.display) {
            addButtonText.style.display = 'flex';
            addButtonText.style.alignItems = 'center';
            addButtonText.style.justifyContent = 'center';
            addButtonText.style.gap = '8px';
          }
          addButtonText.innerHTML = cartIconSvg + '<span>' + window.MinimogStrings.addToCart + '</span>';
        };

        disable ? disableButton() : enableButton();
      });
    }

    updateProductMeta() {
      const { available, sku } = this.currentVariant;
      const { inStock, outOfStock } = window.MinimogStrings;
      const productAvailability = this.productWrapper.querySelector(this.selectors.productAvailability);
      const productSku = this.productWrapper.querySelector(this.selectors.productSku);

      const updateProductSku = () => {
        if (productSku) {
          productSku.textContent = sku ? sku : "N/A";
        }
      };

      const updateProductAvailability = () => {
        if (productAvailability) {
          const method = available ? "remove" : "add";
          productAvailability.textContent = available ? inStock : outOfStock;
          productAvailability.classList[method]("m-product-availability--outofstock");
        }
      };

      updateProductSku();
      updateProductAvailability();
    }

    setUnavailable() {
      const button = document.getElementById(`product-form-${this.sectionId}`);
      const addButton = button ? button.querySelector('[name="add"]') : null;
      const addButtonText = addButton ? addButton.querySelector(".m-add-to-cart--text") : null;
      const priceWrapper = this.productWrapper.querySelector(".m-price");

      const cartIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="19" viewBox="0 0 16 19" fill="none" style="display: inline-block; flex-shrink: 0; width: 16px; height: 19px;"><path d="M8 0L0.256348 13.1318H15.7437L8 0ZM6.87626 0.541482L0 5.44137V12.2023L6.87626 0.541482ZM9.12365 0.541482L16 12.2023V5.44132L9.12365 0.541482ZM3.36209 4.39569V5.32846L2.99313 5.74291V5.01754C2.87261 5.15626 2.74565 5.29014 2.62857 5.43131C2.7823 5.44385 2.73657 5.66379 2.72452 5.76321C2.64861 6.17149 2.37 6.51568 2.14174 6.82459C2.05726 6.93351 1.9703 7.04188 1.88052 7.14601C1.67835 7.29158 1.51139 7.33396 1.41204 7.08337C1.42483 6.74915 1.579 6.43172 1.76187 6.18566L1.99178 5.84665C1.9897 5.95207 1.99613 6.05945 1.98865 6.1637C1.88691 6.34982 1.69448 6.56873 1.7383 6.78914C2.0757 6.82656 2.26961 6.32404 2.3697 6.07927C2.40587 5.9611 2.46135 5.84558 2.30804 5.84661V5.5356L3.36209 4.39569ZM12.5851 4.4475L14.5353 6.77938V7.0903L13.2177 5.53569V5.89842L12.9542 5.58749C12.9475 5.40981 12.8923 5.22883 12.8488 5.0693C12.7679 4.9436 12.6766 4.82509 12.5853 4.70666L12.5851 4.4475ZM8.00304 7.138C8.36696 7.138 8.64359 7.20346 8.83296 7.33439C9.02232 7.46398 9.117 7.65442 9.117 7.90572C9.117 8.07372 9.07 8.21391 8.976 8.32629C8.88336 8.43872 8.74571 8.52204 8.56304 8.57624C8.7671 8.63182 8.92087 8.72244 9.02435 8.84812C9.12907 8.97234 9.18145 9.12967 9.18148 9.32011C9.18148 9.61368 9.08209 9.83589 8.8833 9.98674C8.68591 10.1361 8.39249 10.2108 8.00304 10.2108C7.61223 10.2108 7.31678 10.1361 7.1167 9.9867C6.91791 9.83591 6.81852 9.6137 6.81852 9.32007C6.81852 9.12969 6.87026 8.97235 6.97374 8.84808C7.07843 8.7224 7.23285 8.63177 7.437 8.5762C7.25439 8.52205 7.11675 8.43875 7.02409 8.32629C6.93145 8.21389 6.8851 8.07371 6.88504 7.90576C6.8851 7.65583 6.9798 7.46541 7.16913 7.33448C7.35846 7.20355 7.63644 7.1381 8.00304 7.13813V7.138ZM8.00304 7.61804C7.87954 7.61804 7.78417 7.6498 7.71696 7.71331C7.65116 7.77679 7.61826 7.8667 7.61826 7.98304C7.61826 8.10078 7.65116 8.19138 7.71696 8.25483C7.78417 8.31834 7.87954 8.35009 8.00304 8.35009C8.12391 8.35009 8.21725 8.31834 8.28304 8.25483C8.34884 8.19138 8.38174 8.1008 8.38174 7.98308C8.38174 7.86537 8.34884 7.77546 8.28304 7.71335C8.21725 7.64981 8.12391 7.61804 8.00304 7.61804ZM8.00304 8.82808C7.85797 8.82808 7.74652 8.8671 7.6687 8.94514C7.59072 9.02318 7.55174 9.13492 7.55174 9.28038C7.55174 9.42587 7.59072 9.53761 7.6687 9.61562C7.74652 9.69235 7.85797 9.73071 8.00304 9.73071C8.14672 9.73071 8.25684 9.69236 8.33339 9.61567C8.40991 9.53757 8.44817 9.42579 8.44817 9.28034C8.44817 9.13357 8.40991 9.02182 8.33339 8.9451C8.25687 8.86706 8.14675 8.82804 8.00304 8.82804V8.82808ZM0.726087 13.8168L8 19L15.2739 13.8168H0.726087ZM6.78783 14.8114H9.15948V15.0187C8.53313 15.2731 8.22113 15.5709 7.9473 16.1069H7.26209C7.57096 15.5159 7.89452 15.3485 8.42165 15.0187H6.78774L6.78783 14.8114Z" fill="currentColor"/></svg>';

      const updateButtonState = () => {
        if (addButtonText) {
          addButtonText.innerHTML = window.MinimogStrings.unavailable;
        }
      };

      const hidePrice = () => {
        if (priceWrapper) {
          priceWrapper.classList.add("m:visibility-hidden");
        }
      };

      if (!addButton) return;

      updateButtonState();
      hidePrice();
    }

    hideSoldOutAndUnavailableOptions() {
      const classes = {
        soldOut: "m-product-option--node__soldout",
        unavailable: "m-product-option--node__unavailable",
      };
      const { optionNodes } = this.domNodes;
      const { variants, options } = this.productData;

      optionNodes.forEach((optNode) => {
        const { optionPosition, value } = optNode.dataset;
        const optPos = Number(optionPosition);
        const isSelectOption = optNode.tagName === "OPTION";

        let matchVariants = [];
        if (optPos === options.length) {
          const optionsArray = [...this.currentVariant.options];
          optionsArray[optPos - 1] = value;
          matchVariants.push(getVariantFromOptionArray(this.productData, optionsArray));
        } else {
          matchVariants = variants.filter(
            (v) =>
              v.options[optPos - 1] === value && v.options[optPos - 2] === this.currentVariant[`option${optPos - 1}`]
          );
        }

        matchVariants = matchVariants.filter(Boolean);
        if (matchVariants.length) {
          optNode.classList.remove(classes.unavailable);
          if (isSelectOption) optNode.removeAttribute("disabled");
          const isSoldOut = matchVariants.every((v) => !v.available);
          const method = isSoldOut ? "add" : "remove";
          optNode.classList[method](classes.soldOut);
        } else {
          optNode.classList.add(classes.unavailable);
          if (isSelectOption) optNode.setAttribute("disabled", "true");
        }
      });
    }

    getVariantData() {
      if (!this.variantData) {
        const productVariants = this.querySelector('#productVariants[type="application/json"]');
        if (productVariants) {
          try {
            this.variantData = JSON.parse(productVariants.textContent);
          } catch (error) {
            console.error("Error parsing variant data:", error);
          }
        }
      }
      return this.variantData || {};
    }

    getVariantGroupImageData() {
      if (!this.variantGroupImages) {
        const variantGroupElement = this.querySelector('#variantGroup[type="application/json"]');
        if (variantGroupElement) {
          try {
            this.variantGroupImages = JSON.parse(variantGroupElement.textContent);
          } catch (error) {
            console.error("Error parsing variant group images data:", error);
          }
        }
      }
      return this.variantGroupImages || {};
    }

    initOptionSwatches() {
      const { _colorSwatches = [], _imageSwatches = [] } = window.MinimogSettings;

      if (this.domNodes.optionNodes) {
        this.domNodes.optionNodes.forEach((optNode) => {
          const { optionType, optionPosition, value: optionValue } = optNode.dataset;
          const optionValueLowerCase = optionValue && optionValue.toLowerCase();
          const variantToShowSwatchImage = this.variantData.find((v) => v[`option${optionPosition}`] === optionValue);
          const variantImage =
            variantToShowSwatchImage &&
            variantToShowSwatchImage.featured_image &&
            variantToShowSwatchImage.featured_image.src
              ? getSizedImageUrl(variantToShowSwatchImage.featured_image.src, "100x")
              : null;
          const customImage = _imageSwatches.find((i) => i.key === optionValueLowerCase);
          const customColor = _colorSwatches.find((c) => c.key === optionValueLowerCase);

          if (variantImage || (customImage && customImage.value)) {
            const labelElement = optNode.querySelector("label");
            if (labelElement) {
              labelElement.classList.add("has-bg-img");
            }
          }

          const labelElement = optNode.querySelector("label");
          if (labelElement) {
            labelElement.classList.remove("option-loading");

            switch (optionType) {
              case "default":
              case "image":
                labelElement.style.backgroundImage = `url(${(customImage && customImage.value) || variantImage || ""})`;
                break;
              case "color":
                labelElement.style.backgroundColor = (customColor && customColor.value) || optionValueLowerCase;
                if (customImage && customImage.value) {
                  labelElement.style.backgroundImage = `url(${customImage.value})`;
                }
                break;
              default:
                break;
            }
          }
        });
      }
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
if (!customElements.get("variant-button")) {
  class VariantButton extends HTMLElement {
    constructor() {
      super();
      this.selectedSpan = this.querySelector(".option-label--selected");
      this.addEventListener("change", this.updateSelectedValue);
    }

    updateSelectedValue() {
      this.value = Array.from(this.querySelectorAll("input")).find((radio) => radio.checked).value;
      this.setAttribute("data-selected-value", this.value);
      if (this.selectedSpan) {
        this.selectedSpan.textContent = this.value;
        this.selectedSpan.classList.remove("m:text-color-error");
      }
    }
  }

  customElements.define("variant-button", VariantButton);

  if (!customElements.get("variant-select")) {
    class VariantSelect extends VariantButton {
      constructor() {
        super();
      }

      updateSelectedValue() {
        this.value = this.querySelector("select").value;
        this.setAttribute("data-selected-value", this.value);
        if (this.selectedSpan) {
          this.selectedSpan.textContent = this.value;
          this.selectedSpan.classList.remove("m:text-color-error");
        }
      }
    }

    customElements.define("variant-select", VariantSelect);
  }
  if (!customElements.get("variant-image")) {
    class VariantImage extends VariantButton {
      constructor() {
        super();
      }
    }

    customElements.define("variant-image", VariantImage);
  }
  if (!customElements.get("variant-color")) {
    class VariantColor extends VariantButton {
      constructor() {
        super();
      }
    }
    customElements.define("variant-color", VariantColor);
  }
}
